(() => {
  'use strict';

  if (typeof window === 'undefined' || typeof customElements === 'undefined') return;

  // The native Horizon inline script in layout/theme.liquid only writes
  // --header-height when <header-component> exists. With a winery context
  // active, our custom element replaces it and must publish these vars
  // so dependent sections (transparent header offsets, etc.) keep working.
  const writeHeaderHeightVars = () => {
    const headerGroup = document.getElementById('header-group');
    if (!headerGroup) return;
    const activeHeader = headerGroup.querySelector('.winery-header');
    if (!activeHeader) return;

    let groupHeight = 0;
    for (const el of headerGroup.children) {
      if (el instanceof HTMLElement) groupHeight += el.offsetHeight;
    }

    document.body.style.setProperty('--header-height', `${activeHeader.offsetHeight}px`);
    document.body.style.setProperty('--header-group-height', `${groupHeight}px`);
  };

  if (!customElements.get('winery-header')) {
    customElements.define('winery-header', class extends HTMLElement {
      #header = null;
      #behavior = 'always';
      #threshold = 1;
      #lastScrollY = 0;
      #isSticky = false;
      #onScroll = null;
      #onResize = null;
      #scrolling = false;
      #scrollEndTimer = 0;
      #onScrollActivity = null;
      #initialSticky = false;
      #isSolid = false;

      connectedCallback() {
        this.#behavior = this.getAttribute('data-sticky-behavior') || 'always';
        this.#initialSticky = this.getAttribute('data-initial-sticky') === 'true';
        // The custom element itself carries .winery-header; some legacy
        // renders may nest it, so we fall back to descendant.
        this.#header = this.classList.contains('winery-header')
          ? this
          : this.querySelector('.winery-header');
        if (!this.#header) return;

        if (this.#initialSticky) {
          this.#isSolid = true;
        }

        this.#onResize = () => {
          this.#threshold = this.#computeThreshold();
          writeHeaderHeightVars();
        };
        window.addEventListener('resize', this.#onResize, { passive: true });
        requestAnimationFrame(writeHeaderHeightVars);

        // Actividad de scroll independiente del sticky: un header con blur en
        // estado normal también debe dejar de difuminar mientras se scrollea.
        // Solo si hay blur configurado (clases wim-bf-*); si no, togglear la
        // clase en cada scroll sería trabajo inútil.
        if (this.#header.className.includes('wim-bf-')) {
          this.#onScrollActivity = () => this.#markScrolling();
          window.addEventListener('scroll', this.#onScrollActivity, { passive: true });
        }

        if (this.#behavior === 'never') return;

        this.#threshold = this.#computeThreshold();
        this.#onScroll = () => this.#handleScroll();
        window.addEventListener('scroll', this.#onScroll, { passive: true });
        this.#handleScroll();
      }

      disconnectedCallback() {
        if (this.#onResize) window.removeEventListener('resize', this.#onResize);
        if (this.#onScroll) window.removeEventListener('scroll', this.#onScroll);
        if (this.#onScrollActivity) window.removeEventListener('scroll', this.#onScrollActivity);
        if (this.#scrollEndTimer) clearTimeout(this.#scrollEndTimer);
      }

      #markScrolling() {
        // Mientras el usuario scrollea, suprimimos el backdrop-filter (la clase
        // activa el :not() del CSS); lo restauramos al detenerse para no
        // recomponer la capa difuminada en cada frame.
        if (!this.#scrolling) {
          this.#scrolling = true;
          this.#header.classList.add('winery-header--scrolling');
        }
        if (this.#scrollEndTimer) clearTimeout(this.#scrollEndTimer);
        this.#scrollEndTimer = setTimeout(() => {
          this.#scrolling = false;
          this.#header.classList.remove('winery-header--scrolling');
          this.#scrollEndTimer = 0;
        }, 150);
      }

      #computeThreshold() {
        const bar = document.querySelector('corporate-bar');
        return bar?.offsetHeight || 1;
      }

      #handleScroll() {
        const y = window.scrollY || window.pageYOffset || 0;

        if (this.#initialSticky) {
          if (y >= this.#threshold) {
            this.#setSolid(false);
            this.#setSticky(true);
          } else {
            this.#setSticky(false);
            this.#setSolid(true);
          }
          if (this.#behavior === 'on_scroll_up' && this.#isSticky) {
            this.#header.classList.toggle('winery-header--scrolling-up', y < this.#lastScrollY);
          }
          this.#lastScrollY = y;
          return;
        }

        this.#setSolid(false);
        this.#setSticky(y >= this.#threshold);

        if (this.#behavior === 'on_scroll_up' && this.#isSticky) {
          this.#header.classList.toggle('winery-header--scrolling-up', y < this.#lastScrollY);
        }
        this.#lastScrollY = y;
      }

      #setSolid(isSolid) {
        if (isSolid === this.#isSolid) return;
        this.#isSolid = isSolid;
        this.#header.classList.toggle('winery-header--solid', isSolid);
      }

      #setSticky(isSticky) {
        if (isSticky === this.#isSticky) return;
        this.#isSticky = isSticky;
        this.#header.classList.toggle('winery-header--sticky', isSticky);
        if (!isSticky) this.#header.classList.remove('winery-header--scrolling-up');
        requestAnimationFrame(writeHeaderHeightVars);
      }
    });
  }

  if (!customElements.get('winery-menu-drawer')) {
    customElements.define('winery-menu-drawer', class extends HTMLElement {
      #dialog = null;
      #toggle = null;
      #closeBtn = null;
      #handlers = [];

      connectedCallback() {
        this.#dialog = this.querySelector('.winery-menu-drawer__dialog');
        if (!this.#dialog) return;

        this.#closeBtn = this.#dialog.querySelector('.winery-menu-drawer__close');

        const id = this.getAttribute('id');
        if (id) {
          this.#toggle = document.querySelector(`[aria-controls="${CSS.escape(id)}"]`);
        }

        if (this.#toggle) this.#on(this.#toggle, 'click', (e) => this.#open(e));
        if (this.#closeBtn) this.#on(this.#closeBtn, 'click', () => this.#close());
        // showModal() routes clicks on the backdrop to the <dialog> itself.
        this.#on(this.#dialog, 'click', (e) => { if (e.target === this.#dialog) this.#close(); });
        this.#on(this.#dialog, 'close', () => this.#onClosed());
      }

      disconnectedCallback() {
        for (const [el, evt, fn] of this.#handlers) el.removeEventListener(evt, fn);
        this.#handlers.length = 0;
      }

      #on(el, evt, fn) {
        el.addEventListener(evt, fn);
        this.#handlers.push([el, evt, fn]);
      }

      #open(event) {
        event?.preventDefault();
        if (!this.#dialog?.showModal || this.#dialog.open) return;
        try {
          this.#dialog.showModal();
        } catch {
          return;
        }
        this.#toggle?.setAttribute('aria-expanded', 'true');
        document.documentElement.classList.add('is-drawer-open');
      }

      #close() {
        if (this.#dialog?.open) this.#dialog.close();
      }

      #onClosed() {
        if (this.#toggle) {
          this.#toggle.setAttribute('aria-expanded', 'false');
          try {
            this.#toggle.focus({ preventScroll: true });
          } catch {
            this.#toggle.focus();
          }
        }
        document.documentElement.classList.remove('is-drawer-open');
      }
    });
  }

  if (!customElements.get('corporate-bar')) {
    customElements.define('corporate-bar', class extends HTMLElement {
      #bar = null;
      #toggle = null;
      #sheet = null;
      #sheetCloseBtn = null;
      #mq = null;
      #handlers = [];

      connectedCallback() {
        this.#bar = this.classList.contains('corporate-bar')
          ? this
          : this.querySelector('.corporate-bar');
        if (!this.#bar) return;

        this.#toggle = this.#bar.querySelector('.corporate-bar__collapsed-toggle');
        this.#sheet = this.#bar.querySelector('.corporate-bar__sheet');
        this.#sheetCloseBtn = this.#sheet?.querySelector('.corporate-bar__sheet-close') || null;

        const breakpoint = parseInt(this.getAttribute('data-collapse-breakpoint'), 10) || 1024;

        if (typeof window.matchMedia === 'function') {
          this.#mq = window.matchMedia(`(max-width: ${breakpoint - 1}px)`);
          const onChange = () => this.#applyCollapse();
          this.#applyCollapse();
          this.#mq.addEventListener?.('change', onChange) ?? this.#mq.addListener?.(onChange);
          this.#handlers.push([this.#mq, 'change', onChange]);
        }

        if (this.#toggle && this.#sheet) {
          this.#on(this.#toggle, 'click', (e) => this.#openSheet(e));
          this.#on(this.#sheet, 'click', (e) => { if (e.target === this.#sheet) this.#closeSheet(); });
          this.#on(this.#sheet, 'close', () => this.#onSheetClosed());
          if (this.#sheetCloseBtn) this.#on(this.#sheetCloseBtn, 'click', () => this.#closeSheet());
        }
      }

      disconnectedCallback() {
        for (const [el, evt, fn] of this.#handlers) {
          if (el === this.#mq) {
            el.removeEventListener?.('change', fn) ?? el.removeListener?.(fn);
          } else {
            el.removeEventListener(evt, fn);
          }
        }
        this.#handlers.length = 0;
      }

      #on(el, evt, fn) {
        el.addEventListener(evt, fn);
        this.#handlers.push([el, evt, fn]);
      }

      #applyCollapse() {
        const collapsed = !!this.#mq?.matches;
        this.#bar.classList.toggle('corporate-bar--collapsed', collapsed);
        if (this.#toggle) this.#toggle.hidden = !collapsed;
        if (!collapsed && this.#sheet?.open) this.#sheet.close();
      }

      #openSheet(event) {
        event?.preventDefault();
        if (!this.#sheet?.showModal || this.#sheet.open) return;
        try {
          this.#sheet.showModal();
        } catch {
          return;
        }
        this.#toggle?.setAttribute('aria-expanded', 'true');
        document.documentElement.classList.add('is-drawer-open');
      }

      #closeSheet() {
        if (this.#sheet?.open) this.#sheet.close();
      }

      #onSheetClosed() {
        if (this.#toggle) {
          this.#toggle.setAttribute('aria-expanded', 'false');
          try {
            this.#toggle.focus({ preventScroll: true });
          } catch {
            this.#toggle.focus();
          }
        }
        document.documentElement.classList.remove('is-drawer-open');
      }
    });
  }

  if (!customElements.get('wim-locale-selector')) {
    customElements.define('wim-locale-selector', class extends HTMLElement {
      #toggle = null;
      #menu = null;
      #items = [];
      #handlers = [];
      #onDocClick = null;
      #onDocKey = null;

      connectedCallback() {
        this.#toggle = this.querySelector('[data-wim-locale-toggle]');
        this.#menu = this.querySelector('.wim-winery-locale__menu');
        if (!this.#toggle || !this.#menu) return;
        this.#items = Array.from(this.#menu.querySelectorAll('[role="menuitem"]'));

        this.#on(this.#toggle, 'click', (e) => { e.preventDefault(); this.#toggleOpen(); });
        this.#on(this.#toggle, 'keydown', (e) => this.#handleToggleKeydown(e));
        this.#on(this.#menu, 'keydown', (e) => this.#handleMenuKeydown(e));

        this.#onDocClick = (e) => { if (!this.contains(e.target)) this.#close(); };
        this.#onDocKey = (e) => { if (e.key === 'Escape' && this.#isOpen()) this.#close(true); };
        document.addEventListener('click', this.#onDocClick);
        document.addEventListener('keydown', this.#onDocKey);
      }

      disconnectedCallback() {
        for (const [el, evt, fn] of this.#handlers) el.removeEventListener(evt, fn);
        this.#handlers.length = 0;
        if (this.#onDocClick) document.removeEventListener('click', this.#onDocClick);
        if (this.#onDocKey) document.removeEventListener('keydown', this.#onDocKey);
      }

      #on(el, evt, fn) {
        el.addEventListener(evt, fn);
        this.#handlers.push([el, evt, fn]);
      }

      #isOpen() {
        return this.#toggle.getAttribute('aria-expanded') === 'true';
      }

      #toggleOpen() {
        if (this.#isOpen()) this.#close(true); else this.#open();
      }

      #open() {
        this.#toggle.setAttribute('aria-expanded', 'true');
        this.#menu.hidden = false;
        const first = this.#items.find(i => i.classList.contains('wim-winery-locale__option--active')) || this.#items[0];
        first?.focus();
      }

      #close(returnFocus = false) {
        if (!this.#isOpen()) return;
        this.#toggle.setAttribute('aria-expanded', 'false');
        this.#menu.hidden = true;
        if (returnFocus) this.#toggle.focus();
      }

      #handleToggleKeydown(e) {
        if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.#open();
        }
      }

      #handleMenuKeydown(e) {
        const idx = this.#items.indexOf(document.activeElement);
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          this.#items[(idx + 1) % this.#items.length]?.focus();
        } else if (e.key === 'ArrowUp') {
          e.preventDefault();
          this.#items[(idx - 1 + this.#items.length) % this.#items.length]?.focus();
        } else if (e.key === 'Home') {
          e.preventDefault();
          this.#items[0]?.focus();
        } else if (e.key === 'End') {
          e.preventDefault();
          this.#items[this.#items.length - 1]?.focus();
        } else if (e.key === 'Tab') {
          this.#close();
        }
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => requestAnimationFrame(writeHeaderHeightVars));
  } else {
    requestAnimationFrame(writeHeaderHeightVars);
  }
})();

/* Reservar CTA: si ya estás en la página que tiene el widget (#reservar), hace scroll en vez de navegar. */
(() => {
  'use strict';

  const closeOpenDrawer = () => {
    const drawer = document.querySelector('winery-menu-drawer');
    if (!drawer) return;
    // Best-effort: cerrar pulsando el toggle abierto o enviando Escape al drawer.
    const toggle = document.querySelector('.winery-header [aria-expanded="true"]');
    if (toggle) { toggle.click(); return; }
    drawer.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape', bubbles: true }));
  };

  const smoothScrollTo = (id) => {
    const target = document.getElementById(id);
    if (!target) return false;
    closeOpenDrawer();
    requestAnimationFrame(() => {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      history.replaceState?.(null, '', `#${id}`);
    });
    return true;
  };

  document.addEventListener('click', (e) => {
    const a = e.target.closest('.winery-header__cta, .winery-menu-drawer__cta, a[href*="#reservar"]');
    if (!a) return;

    const href = a.getAttribute('href') || '';
    const hashIndex = href.indexOf('#');
    if (hashIndex !== -1) {
      const anchor = href.slice(hashIndex + 1);
      if (anchor && document.getElementById(anchor)) {
        e.preventDefault();
        smoothScrollTo(anchor);
        return;
      }
    }

    // Enlace a la propia página + existe #reservar -> scroll en lugar de recargar.
    try {
      const url = new URL(a.href, location.href);
      if (url.pathname === location.pathname && document.getElementById('reservar')) {
        e.preventDefault();
        smoothScrollTo('reservar');
      }
    } catch {}
  });
})();
